import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  RUNNING_BUILD,
  buildLabel,
  fetchBuildInfo,
  versionStatus,
  type BuildInfo,
  type VersionStatus,
} from '@/lib/buildInfo';

export const WEB_BUILD_URL = `${import.meta.env.BASE_URL}build.json`;

/** Running bundle vs the deployed /web/build.json; re-checked when the app regains focus. */
export function useVersionStatus(): { status: VersionStatus | null; live: BuildInfo | null } {
  const web = useQuery({
    queryKey: ['build', 'web'],
    queryFn: ({ signal }) => fetchBuildInfo(WEB_BUILD_URL, signal),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  return {
    status: web.isPending ? null : versionStatus(RUNNING_BUILD, web.data ?? null),
    live: web.data ?? null,
  };
}

/**
 * Load the newest deployed bundle. With autoUpdate the service worker takes over as soon as
 * it has installed; update() alone can resolve before that, so wait (time-boxed) for the
 * controller change before reloading, or the reload may still be served the old bundle.
 */
export async function reloadToLatest(): Promise<void> {
  const sw = 'serviceWorker' in navigator ? navigator.serviceWorker : null;
  if (sw) {
    const reg = await sw.getRegistration();
    const takenOver = new Promise<void>((resolve) => {
      sw.addEventListener('controllerchange', () => resolve(), { once: true });
      setTimeout(resolve, 5000);
    });
    await reg?.update().catch(() => undefined);
    if (reg?.installing || reg?.waiting) await takenOver;
  }
  window.location.reload();
}

/**
 * The version line under the Settings menu item (phone "More" menu and desktop sidebar).
 * Turns into an update button when a newer version is live.
 */
export function VersionMenuLine({ tone }: { tone: 'light' | 'dark' }) {
  const { status } = useVersionStatus();
  const [updating, setUpdating] = useState(false);
  const muted = tone === 'dark' ? 'text-slate-500' : 'text-slate-400';

  if (status?.status === 'outdated') {
    return (
      <button
        type="button"
        className={`w-full text-left text-xs font-medium touch-manipulation ${
          tone === 'dark' ? 'text-amber-300 hover:text-amber-200' : 'text-amber-700 hover:text-amber-800'
        }`}
        disabled={updating}
        onClick={() => {
          setUpdating(true);
          void reloadToLatest();
        }}
      >
        {updating ? 'Updating…' : 'Update available: tap to update'}
        <span className={`block font-normal ${muted}`}>This device: {buildLabel(RUNNING_BUILD)}</span>
      </button>
    );
  }
  return <p className={`text-xs ${muted}`}>Version {buildLabel(RUNNING_BUILD)}</p>;
}
